import { connect } from 'react-redux';
import { cloneDeep, has, isNil, isString, set } from 'lodash';
import { AnyAction as BaseAction } from 'redux';
import type { AppThunkDispatch } from 'src/store.types';
import type { AppState } from 'src/reducers';
import service from 'src/ServiceContainer';
import Axios from 'src/services/axios';
import { DataApi, isListDataApi } from 'configuration-schemas/confdefnView';
import { AppType } from 'configuration-schemas/general';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { getUrl, processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { receiveError, receiveGridData, requestGridData } from 'src/components/ConfigurableGrid/ConfigurableGrid.slice';
import { getSelectedFloorset } from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';
import { EditableOverlay, EditableOverlayDispatchProps } from './EditableOverlay';
import { isEmpty } from 'validator';

/**
 * Submit to overlay pivot and load the results
 */
export function submitEditableOverlayData(dataApi: DataApi, scope: Record<string, unknown>) {
  return async (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const processedApi = processApiParams(dataApi, scope);
    dispatch(requestGridData());

    // Note the `topMember` vs `topMembers` (PLURAL with an `s` on the end)
    // `getSelectedFloorset` is just one, and it's the configured floorset/quarter etc only
    // we have to pull from worklist to create the full set of `topMembers` that may need to go into the pivot
    // NOTE: when in a non-worklist `ConfigurableGrid` this doesn't pull the companion item, as that is stored in
    // local `ConfigurableGrid` state and isn't easy to access from here
    const maybeTopMemberSingular = getSelectedFloorset(getState());
    const maybeWorklistItem = getState().worklist.selectedItemId;
    const topMembers = [maybeTopMemberSingular, maybeWorklistItem].filter((v) => !isNil(v) && !isEmpty(v)) as string[];

    try {
      let formattedData: BasicPivotItem[];
      if (isListDataApi(processedApi)) {
        if (has(processApiParams, 'params.topMembers')) {
          // reminder that `topMembers` is processed into a string by now,
          // so it needs to be parsed into a string
          // I'm looking forward to getting some weird but where the fetch starts with a comma someday
          // @ts-ignore
          processApiParams.params.topMemberss += `,${topMembers.join(',')}`;
        } else {
          set(processApiParams, 'params.topMembers', topMembers.join(','));
        }
        const resp = await service.pivotService.listData(processedApi.defnId, AppType.Assortment, {
          ...processedApi.params,
          nestData: false, // ConfigurableGrid requires flat responses
        });
        formattedData = resp.tree;
      } else {
        const { headers, params } = processedApi;
        const resp = await Axios.get(getUrl(processedApi), { headers, params });
        const dataResponse = resp.data;
        formattedData = isString(dataResponse) ? service.pivotService.deserialize(dataResponse) : dataResponse.list;
        if (formattedData?.[0]?.children?.length) {
          formattedData = formattedData[0].children;
        }
      }

      const clonedFormattedData = cloneDeep(formattedData);
      clonedFormattedData?.forEach((attr: BasicPivotItem) => {
        Object.keys(attr).forEach((k) => (attr[k] = isString(attr[k]) ? attr[k].trim() : attr[k]));
      });

      // only works with `ConfigurableGrid` currently
      dispatch(receiveGridData(clonedFormattedData));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.configurableGrid,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

function mapDispatchToProps(dispatch: AppThunkDispatch): EditableOverlayDispatchProps {
  return {
    onSubmit: (dataApi, scope) => {
      dispatch(submitEditableOverlayData(dataApi, scope));
    },
  };
}

export default connect(null, mapDispatchToProps)(EditableOverlay);
